import { Language } from '@/lib/interfaces/interfaces'
import { CartItem } from '@/lib/interfaces/ordering'
import {
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Box
} from '@mui/material'
import { Minus, Plus, Trash2, X } from 'lucide-react'

// Objeto de traducciones para cada idioma
const translations = {
  es: {
    orderTitle: 'Tu Pedido',
    closeCart: 'Cerrar carrito',
    removeProduct: 'Eliminar producto',
    decrement: 'Decrementar cantidad',
    increment: 'Incrementar cantidad',
    price: 'Precio'
  },
  en: {
    orderTitle: 'Your Order',
    closeCart: 'Close cart',
    removeProduct: 'Remove product',
    decrement: 'Decrease quantity',
    increment: 'Increase quantity',
    price: 'Price'
  },
  fr: {
    orderTitle: 'Votre commande',
    closeCart: 'Fermer le panier',
    removeProduct: 'Supprimer le produit',
    decrement: 'Diminuer la quantité',
    increment: 'Augmenter la quantité',
    price: 'Prix'
  }
}

interface Props {
  toggleCart: () => void
  cart: CartItem[]
  removeFromCart: (itemId: string, variantType: string) => void
  handleQuantityChange: (itemId: string, variantType: string, quantity: number) => void
  language: Language
}

const CartList = ({
  cart,
  handleQuantityChange,
  removeFromCart,
  toggleCart,
  language
}: Props) => {
  const texts = translations[language] || translations.es

  return (
    <>
      <div className='orderingCart__container__header'>
        <Typography
          variant='h6'
          className='orderingCart__container__header__title'
        >
          {texts.orderTitle}
        </Typography>
        <IconButton
          onClick={toggleCart}
          aria-label={texts.closeCart}
          className='orderingCart__container__header__closeButton'
        >
          <X size={20} />
        </IconButton>
      </div>
      <Divider />

      {/* Lista de productos en el carrito */}
      <div className='orderingCart__container__productList'>
        <List>
          {cart.map((item) => {
            // Definir indicador: "1/2" para media y "1/3" para pequeña. Si es grande (full) no se muestra.
            const variantIndicator =
              item.variant.type === 'half'
                ? '1/2'
                : item.variant.type === 'tapa'
                  ? '1/3'
                  : ''
            return (
              <ListItem
                key={`${item.id}-${item.variant.type}`}
                className='productItem'
              >
                <IconButton
                  size='small'
                  onClick={() =>
                    removeFromCart(item.id, item.variant.type)}
                  className='removeButton'
                  aria-label={texts.removeProduct}
                >
                  <Trash2 size={16} />
                </IconButton>
                <ListItemText
                  primary={
                    <Box display='flex' alignItems='center'>
                      <Typography variant='body1'>
                        {item.name} {variantIndicator && <span className='variant-tag'>{variantIndicator}</span>}
                      </Typography>
                    </Box>
                  }
                  secondary={`${texts.price}: ${item.variant.price.toFixed(2)}€`}
                />
                <div className='orderingCart__container__productList__productItem__actions'>
                  <div className='quantityControl'>
                    <IconButton
                      size='small'
                      onClick={() =>
                        handleQuantityChange(
                          item.id,
                          item.variant.type,
                          item.quantity - 1
                        )}
                      aria-label={texts.decrement}
                      className='iconButton'
                    >
                      <Minus size={16} />
                    </IconButton>
                    <TextField
                      size='small'
                      type='number'
                      value={item.quantity}
                      onChange={(e) => {
                        const val = parseInt(e.target.value)
                        handleQuantityChange(
                          item.id,
                          item.variant.type,
                          isNaN(val) || val < 1 ? 1 : val
                        )
                      }}
                      inputProps={{
                        min: 1,
                        className: 'quantityInput'
                      }}
                      className='quantityInput'
                    />
                    <IconButton
                      size='small'
                      onClick={() =>
                        handleQuantityChange(
                          item.id,
                          item.variant.type,
                          item.quantity + 1
                        )}
                      aria-label={texts.increment}
                      className='iconButton'
                    >
                      <Plus size={16} />
                    </IconButton>
                  </div>
                </div>
              </ListItem>
            )
          })}
        </List>
      </div>
    </>
  )
}

export default CartList
